/* eslint-disable max-lines */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import UseAxios from '../../../Api/http/http';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { FINANCE_EMPLOYEE_COST } from '../../../Api/resources/endPoint';
import { useAuth } from '../../../Hooks/useAuth';
import './EmployeeCostDetails.scss';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { buildEmployeeCostUserInfo } from './employeeCostUserInfo';
import { getRoleExperienceMappings } from '../../../Api/request/projectDetails';

const normalizeText = (value) => String(value ?? '').trim();

const normalizeExperienceLabel = (value) => normalizeText(value)
  .replace(/\s*(yrs?|years?)$/i, '')
  .replace(/\s*-\s*/g, '-')
  .replace(/\s*\+\s*$/g, '+')
  .replace(/\s+/g, ' ');

const getExperienceSortParts = (value) => {
  const label = normalizeExperienceLabel(value).toLowerCase();
  const compactLabel = label.replace(/[\s_-]/g, '');

  if (compactLabel === 'junior') return { group: 1, start: 0, end: 0 };
  if (compactLabel === 'midlevel') return { group: 1, start: 1, end: 1 };
  if (compactLabel === 'senior') return { group: 1, start: 2, end: 2 };

  const rangeMatch = label.match(/^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)/);
  if (rangeMatch) {
    return { group: 0, start: Number(rangeMatch[1]), end: Number(rangeMatch[2]) };
  }

  const plusMatch = label.match(/^(\d+(?:\.\d+)?)\+$/);
  if (plusMatch) {
    return { group: 0, start: Number(plusMatch[1]), end: Number(plusMatch[1]) };
  }

  const numericMatch = label.match(/^(\d+(?:\.\d+)?)$/);
  if (numericMatch) {
    return { group: 2, start: Number(numericMatch[1]), end: Number(numericMatch[1]) };
  }

  return { group: 3, start: 0, end: 0 };
};

const sortExperienceOptions = (options = [], getLabel = (option) => option?.label ?? option) => (
  [...options].sort((first, second) => {
    const firstLabel = normalizeExperienceLabel(getLabel(first));
    const secondLabel = normalizeExperienceLabel(getLabel(second));
    const firstSort = getExperienceSortParts(firstLabel);
    const secondSort = getExperienceSortParts(secondLabel);

    return firstSort.group - secondSort.group
      || firstSort.start - secondSort.start
      || firstSort.end - secondSort.end
      || firstLabel.localeCompare(secondLabel, undefined, { numeric: true, sensitivity: 'base' });
  })
);

const buildRoleExperienceLookup = (roleExperienceMappings = []) => {
  const lookup = new Map();

  roleExperienceMappings.forEach((mapping, index) => {
    const roleId = normalizeText(mapping.roleId ?? mapping.role_id);
    const experienceId = normalizeText(
      mapping.experienceId
        ?? mapping.experience_id
        ?? mapping.experienceCategoryId
        ?? mapping.experience_category_id,
    );
    const experienceLabel = normalizeText(
      mapping.experienceRange
        ?? mapping.experience_range
        ?? mapping.experience
        ?? mapping.label,
    ).toLowerCase();

    if (!roleId) return;

    if (!lookup.has(roleId)) {
      lookup.set(roleId, { ids: new Set(), labels: new Map() });
    }

    if (experienceId) lookup.get(roleId).ids.add(experienceId);
    if (experienceLabel) {
      lookup.get(roleId).labels.set(experienceLabel, index);
    }
  });

  return lookup;
};

const mapToOptions = (list) =>
  (list || [])
    .filter((it) => {
      if (it && typeof it === 'object') {
        return it.label !== undefined && it.label !== null && String(it.label).trim() !== '';
      }
      return it !== undefined && it !== null && String(it).trim() !== '';
    })
    .map((it) => {
      if (it && typeof it === 'object') {
        return {
          value: it.value !== undefined ? it.value : it,
          label: it.label !== undefined ? it.label : String(it),
          region_id: it.region_id,
        };
      }
      return {
        value: it,
        label: String(it),
      };
    });

const buildDialogOptions = (options = {}, form = {}, roleExperienceMappings = []) => {
  const roleExperienceLookup = buildRoleExperienceLookup(roleExperienceMappings);
  const allowedExperiences = roleExperienceLookup.get(normalizeText(form.role_id));
  const allExperiences = mapToOptions(options.experiences || options.experienceCategories || []);
  const experiences = form.role_id && allowedExperiences
    ? sortExperienceOptions(
      allExperiences.filter((option) => allowedExperiences.labels.has(
        normalizeText(option.label).toLowerCase(),
      )),
    )
    : [];

  return {
    ...options,
    experiences,
    employeeTypes: ['Regular', 'Contract'],
    departments: options.departments || [],
    locations: options.locations || [],
  };
};

const EmployeeCostAddForm = ({ form, handleChange, opts, loading, errors, isEdit }) => {
  const errorList = (errors || []).map((errMsg, idx) => <li key={idx}>{errMsg}</li>);

  return (
    <div className="employee-cost-add-body">

      <div className="employee-cost-add-row">
        <div className="employee-cost-add-col">
          <label>Select Year*</label>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              views={['year']}
              value={form.year ? dayjs(String(form.year), 'YYYY') : null}
              onChange={(val) => handleChange('year', val ? val.format('YYYY') : null)}
              disabled={isEdit}
              slotProps={{
                popper: {
                  sx: {
                    '& .MuiPaper-root': {
                      height: 'auto !important',
                      minHeight: 'unset !important',
                    },
                    '& .MuiPickersLayout-root': {
                      height: 'auto !important',
                      minHeight: 'unset !important',
                    },
                    '& .MuiDateCalendar-root': {
                      height: 'auto !important',
                      minHeight: 'unset !important',
                    },
                    '& .MuiPickersLayout-contentWrapper': {
                      height: 'auto !important',
                      minHeight: 'unset !important',
                    },
                    '& .MuiYearCalendar-root': {
                      maxHeight: '200px !important',
                    },
                  },
                },
                textField: {
                  size: 'small',
                  placeholder: loading ? 'Loading…' : 'Select',
                  fullWidth: true,
                  sx: {
                    '& .MuiInputBase-root': {
                      height: '42px',
                      borderRadius: '4px',
                      border: '1px solid #ced4da',
                      fontFamily: 'Mukta-Regular, sans-serif',
                      fontSize: '14px',
                      backgroundColor: isEdit ? '#f2f2f2' : '#ffffff',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '& .MuiInputBase-input': {
                      padding: '0 12px',
                    },
                    '& .MuiInputAdornment-root': {
                      marginRight: '4px',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '22px !important',
                      width: '22px !important',
                      height: '22px !important',
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </div>
        <div className="employee-cost-add-col">
          <label>Select Month*</label>
          <Dropdown
            value={form.month}
            options={mapToOptions(opts.months)}
            onChange={(ev) => handleChange('month', ev.value)}
            placeholder={loading ? 'Loading…' : 'Select'}
            disabled={isEdit}
            filter
            filterPlaceholder="Search..."
            panelClassName="employee-cost-dropdown-panel"
            appendTo={document.body}
            scrollHeight="220px"
          />
        </div>
      </div>

      <div className="employee-cost-add-row">
        <div className="employee-cost-add-col">
          <label>Employee Region*</label>
          <Dropdown
            value={form.region_id}
            options={mapToOptions(opts.regions)}
            onChange={(ev) => handleChange('region_id', ev.value)}
            placeholder={loading ? 'Loading…' : 'Select'}
            showClear
            filter
            filterPlaceholder="Search..."
            panelClassName="employee-cost-dropdown-panel"
            appendTo={document.body}
            scrollHeight="220px"
          />
        </div>
        <div className="employee-cost-add-col">
          <label>Employee Country*</label>
          <Dropdown
            value={form.country_id}
            options={mapToOptions(opts.countries).filter(
              (country) => isCountryInRegion(country, form.region_id),
            )}
            onChange={(ev) => handleChange('country_id', ev.value)}
            placeholder={loading ? 'Loading…' : 'Select'}
            showClear
            disabled={!form.region_id}
            filter
            filterPlaceholder="Search..."
            panelClassName="employee-cost-dropdown-panel"
            appendTo={document.body}
            scrollHeight="220px"
          />
        </div>
      </div>

      <div className="employee-cost-add-row">
        <div className="employee-cost-add-col">
          <label>Employee Vertical*</label>
          <Dropdown
            value={form.vertical_id}
            options={mapToOptions(opts.verticals)}
            onChange={(ev) => handleChange('vertical_id', ev.value)}
            placeholder={loading ? 'Loading…' : 'Select'}
            showClear
            filter
            filterPlaceholder="Search..."
            panelClassName="employee-cost-dropdown-panel"
            appendTo={document.body}
            scrollHeight="220px"
          />
        </div>
        <div className="employee-cost-add-col">
          <label>Employee Role*</label>
          <Dropdown
            value={form.role_id}
            options={mapToOptions(opts.roles)}
            onChange={(ev) => handleChange('role_id', ev.value)}
            placeholder={loading ? 'Loading…' : 'Select'}
            showClear
            filter
            filterPlaceholder="Search..."
            panelClassName="employee-cost-dropdown-panel"
            appendTo={document.body}
            scrollHeight="220px"
          />
        </div>
      </div>

      <div className="employee-cost-add-row">
        <div className="employee-cost-add-col">
          <label>Employee Contract Type*</label>
          <Dropdown
            value={form.employee_type}
            options={mapToOptions(opts.employeeTypes)}
            onChange={(ev) => handleChange('employee_type', ev.value)}
            placeholder={loading ? 'Loading…' : 'Select'}
            showClear
            filter
            filterPlaceholder="Search..."
            panelClassName="employee-cost-dropdown-panel"
            appendTo={document.body}
            scrollHeight="220px"
          />
        </div>
        <div className="employee-cost-add-col">
          <label>Employee Location*</label>
          <Dropdown
            value={form.location_id}
            options={mapToOptions(opts.locations)}
            onChange={(ev) => handleChange('location_id', ev.value)}
            placeholder={loading ? 'Loading…' : 'Select'}
            showClear
            filter
            filterPlaceholder="Search..."
            panelClassName="employee-cost-dropdown-panel"
            appendTo={document.body}
            scrollHeight="220px"
          />
        </div>
      </div>

      <div className="employee-cost-add-row">
        <div className="employee-cost-add-col">
          <label>Experience Category*</label>
          <Dropdown
            value={form.experience}
            options={mapToOptions(opts.experiences)}
            onChange={(ev) => handleChange('experience', ev.value)}
            placeholder={loading ? 'Loading…' : 'Select'}
            showClear
            disabled={!form.role_id}
            filter
            filterPlaceholder="Search..."
            panelClassName="employee-cost-dropdown-panel"
            appendTo={document.body}
            scrollHeight="220px"
          />
        </div>
        <div className="employee-cost-add-col" />
      </div>

      <div className="employee-cost-add-row">
        <div className="employee-cost-add-col">
          <label>Employee per hr Cost*</label>
          <InputText
            type="number"
            value={form.cost_per_hour}
            placeholder="Enter Employee per hr Cost"
            onChange={(ev) => handleChange('cost_per_hour', ev.target.value)}
          />
        </div>
        <div className="employee-cost-add-col">
          <label>Employee Department*</label>
          <Dropdown
            value={form.department_name}
            options={mapToOptions(opts.departments)}
            onChange={(ev) => handleChange('department_name', ev.value)}
            placeholder={loading ? 'Loadingâ€¦' : 'Select'}
            showClear
            filter
            filterPlaceholder="Search..."
            panelClassName="employee-cost-dropdown-panel"
            appendTo={document.body}
            scrollHeight="220px"
          />
        </div>
      </div>

      {errors && errors.length ? (
        <div className="employee-cost-upload-error" style={{ marginTop: 12 }}>
          <ul>
            {errorList}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

EmployeeCostAddForm.propTypes = {
  form: PropTypes.shape({
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    month: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    region_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    country_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    vertical_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    role_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    location_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    employee_type: PropTypes.string,
    experience: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    cost_per_hour: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    department_name: PropTypes.string,
  }).isRequired,
  handleChange: PropTypes.func.isRequired,
  opts: PropTypes.object,
  loading: PropTypes.bool,
  errors: PropTypes.arrayOf(PropTypes.string),
  isEdit: PropTypes.bool,
};

EmployeeCostAddForm.defaultProps = {
  opts: {},
  loading: false,
  errors: [],
  isEdit: false,
};

const resolveIdFromLabel = (optionsList, label) => {
  if (!label || !optionsList) return null;
  const match = optionsList.find(
    (opt) =>
      String(opt.label || opt).trim()
        .toLowerCase()
      === String(label).trim()
        .toLowerCase(),
  );
  return match ? match.value || match : null;
};

const isCountryInRegion = (countryOpt, regionId) => {
  if (!regionId) return true;
  return String(countryOpt.region_id) === String(regionId);
};

const mapExperienceToBucket = (expVal) => {
  if (expVal === null || expVal === undefined) return null;
  const numeric = Number(expVal);
  if (Number.isNaN(numeric)) {
    const clean = String(expVal)
      .trim()
      .toLowerCase();
    if (clean.includes('10')) return '> 10';
    return expVal;
  }
  if (numeric < 2) return '0-2';
  if (numeric >= 2 && numeric < 4) return '2-4';
  if (numeric >= 4 && numeric < 6) return '4-6';
  if (numeric >= 6 && numeric < 8) return '6-8';
  if (numeric >= 8 && numeric < 10) return '8-10';
  return '> 10';
};

const buildEditFormState = (editData, options) => {
  const year = editData.month_end_date
    ? String(editData.month_end_date.split('-')[0])
    : null;
  const month = editData.month_end_date
    ? String(editData.month_end_date.split('-')[1]).padStart(2, '0')
    : null;
  const mapEmpType = (typeVal) => {
    if (!typeVal) return null;
    const clean = String(typeVal)
      .trim()
      .toLowerCase();
    if (clean === 'regular' || clean === 'r') return 'Regular';
    if (clean === 'contract' || clean === 'contractor' || clean === 'c') return 'Contract';
    return typeVal;
  };

  return {
    year,
    month,
    region_id: resolveIdFromLabel(options.regions, editData.region_name),
    country_id: resolveIdFromLabel(options.countries, editData.country_name),
    vertical_id: resolveIdFromLabel(options.verticals, editData.vertical_name),
    role_id: resolveIdFromLabel(options.roles, editData.role_name),
    location_id: resolveIdFromLabel(options.locations, editData.location_name),
    employee_type: mapEmpType(editData.employee_type),
    experience: mapExperienceToBucket(editData.experience),
    cost_per_hour:
      editData.hourly_cost !== null && editData.hourly_cost !== undefined
        ? String(editData.hourly_cost)
        : '',
    department_name: resolveIdFromLabel(options.departments, editData.department_name)
      || editData.department_name
      || '',
  };
};

const EmployeeCostAddDialog = ({ open, onClose, onSuccess, editData }) => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({});
  const [roleExperienceMappings, setRoleExperienceMappings] = useState([]);
  const [form, setForm] = useState({
    year: null,
    month: null,
    region_id: null,
    country_id: null,
    vertical_id: null,
    role_id: null,
    location_id: null,
    experience: null,
    cost_per_hour: '',
    department_name: '',
  });
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (!open) return undefined;

    const load = async () => {
      setLoading(true);
      try {
        const filterPayload = {
          userInfo: buildEmployeeCostUserInfo(userProfile),
        };
        const [resp, roleExperienceMappingsResp] = await Promise.all([
          UseAxios(
            {
              service: serviceName.RESELLER_SERVICE,
              method: 'post',
              url: `${FINANCE_EMPLOYEE_COST}/filters`,
              headers: { token: userProfile?.access_token || '' },
              payload: filterPayload,
            },
            () => {},
          ),
          UseAxios(getRoleExperienceMappings()).catch(() => ({ data: { roleExperienceMappings: [] } })),
        ]);
        const nextRoleExperienceMappings = roleExperienceMappingsResp?.data?.roleExperienceMappings
          || roleExperienceMappingsResp?.data?.data?.roleExperienceMappings
          || roleExperienceMappingsResp?.data?.data
          || [];

        if (resp && resp.status === 200 && resp.data) {
          const payload = resp.data.data || resp.data || {};
          setOptions(payload.filterOptions || payload || {});
        } else {
          setOptions({});
        }
        setRoleExperienceMappings(
          Array.isArray(nextRoleExperienceMappings) ? nextRoleExperienceMappings : [],
        );
      } catch (err) {
        setOptions({});
        setRoleExperienceMappings([]);
      } finally {
        setLoading(false);
      }
    };

    load();
    return undefined;
  }, [open, userProfile]);

  useEffect(() => {
    if (!open) {
      setForm({
        year: null,
        month: null,
        region_id: null,
        country_id: null,
        vertical_id: null,
        role_id: null,
        location_id: null,
        employee_type: null,
        experience: null,
        cost_per_hour: '',
        department_name: '',
      });
      setErrors([]);
      return;
    }

    if (editData && Object.keys(options).length > 0) {
      setForm(buildEditFormState(editData, options));
    }
  }, [open, editData, options]);

  const handleChange = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'region_id') {
        next.country_id = null;
      }
      if (key === 'role_id') {
        next.role_id = value ? String(value) : null;
        next.experience = null;
      }
      return next;
    });
  };

  const validate = () => {
    const errs = [];
    if (!form.year) errs.push('Year is required');
    if (!form.month) errs.push('Month is required');
    if (!form.vertical_id) errs.push('Employee Vertical is required');
    if (!form.role_id) errs.push('Employee Role is required');
    if (!form.location_id) errs.push('Employee Location is required');
    if (!form.employee_type) errs.push('Employee Contract Type is required');
    if (!form.experience) errs.push('Experience Category is required');
    if (!form.cost_per_hour && form.cost_per_hour !== 0) errs.push('Employee per hr Cost is required');
    if (!form.department_name) errs.push('Employee Department is required');
    return errs;
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    setErrors(validationErrors);
    if (validationErrors.length) return;

    // Build month_end_date as last day of selected month
    const yearNum = Number(String(form.year));
    const monthNum = Number(String(form.month));
    const lastDay = new Date(yearNum, monthNum, 0).getDate();
    const month_end_date = `${yearNum}-${String(monthNum).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

    const payload = {
      year: form.year,
      month: form.month,
      month_end_date,
      region_id: form.region_id || null,
      country_id: form.country_id || null,
      vertical_id: form.vertical_id || null,
      role_id: form.role_id || null,
      location_id: form.location_id || null,
      employee_type: form.employee_type || null,
      experience: form.experience || null,
      cost_per_hour: form.cost_per_hour === '' ? null : Number(form.cost_per_hour),
      department_id: form.department_name || null,
      department_name: null,
      ...(editData ? { employee_id: String(editData.emp_id), id: editData.id } : {}),
      userInfo: buildEmployeeCostUserInfo(userProfile),
    };

    try {
      setLoading(true);
      const request = {
        service: serviceName.RESELLER_SERVICE,
        method: 'post',
        url: `${FINANCE_EMPLOYEE_COST}/create`,
        payload,
        headers: {
          token: userProfile?.access_token || '',
          'Content-Type': 'application/json',
        },
      };
      const resp = await UseAxios(request, () => {});

      if (resp && (resp.status === 201 || resp.status === 200) && resp.data?.success !== false) {
        onSuccess?.(resp.data);
      } else {
        const errObj = resp?.data;
        const errData = errObj?.response?.data;
        const backendErrors = errData?.data?.errors || errData?.errors || resp?.data?.data?.errors;
        const msg = Array.isArray(backendErrors) && backendErrors.length
          ? backendErrors.join('; ')
          : (errData?.error || errData?.message || errObj?.message || resp?.data?.message || 'Create failed');
        setErrors([msg]);
      }
    } catch (err) {
      // Axios stores the backend response payload inside err.response.data
      const backendErrors = err?.response?.data?.data?.errors || err?.response?.data?.errors;
      const msg = Array.isArray(backendErrors) && backendErrors.length
        ? backendErrors.join('; ')
        : (err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Create failed');
      setErrors([msg]);
    } finally {
      setLoading(false);
    }
  };

  const dialogHeader = (
    <span className="p-dialog-title">
      {editData ? 'Edit Employee Hourly Cost Details' : 'Add Employee Hourly Cost Details'}
    </span>
  );

  const dialogFooter = (
    <div className="employee-cost-dialog-footer">
      <Button
        label="Cancel"
        className="p-button p-button-text"
        onClick={onClose}
        disabled={loading}
      />
      <Button
        // Dynamically switches between 'Update' (Edit mode) and 'Save' (Add mode)
        label={editData ? 'Update' : 'Save'}
        className="p-button p-button-add"
        onClick={handleSubmit}
        disabled={loading}
      />
    </div>
  );

  const opts = useMemo(
    () => buildDialogOptions(options, form, roleExperienceMappings),
    [options, form, roleExperienceMappings],
  );
  return (
    <Dialog
      visible={open}
      onHide={onClose}
      header={dialogHeader}
      footer={dialogFooter}
      className="employee-cost-dialog"
      style={{ width: '680px', maxWidth: '92vw' }}
      modal
      draggable={false}
      resizable={false}
    >
      <EmployeeCostAddForm
        form={form}
        handleChange={handleChange}
        opts={opts}
        loading={loading}
        errors={errors}
        isEdit={Boolean(editData)}
      />
    </Dialog>
  );
};

EmployeeCostAddDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  editData: PropTypes.object,
};

EmployeeCostAddDialog.defaultProps = {
  onSuccess: null,
  editData: null,
};

export default EmployeeCostAddDialog;

